import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Inbox, MailOpen, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "kch_admin_token";

const inputCls =
  "w-full bg-white border border-silver px-5 py-4 text-sm text-navy-dark placeholder:text-navy-dark/40 focus:outline-none focus:border-navy transition-colors duration-300";

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-silver-light flex items-center justify-center px-6">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-md bg-white border border-silver p-10 md:p-12"
      >
        <img src="/logo.png" alt="Karen's Caring Hands, LLC" className="h-16 w-auto" />
        <p className="mt-4 text-[10px] font-mono uppercase tracking-[0.3em] text-navy-dark/50">
          Admin sign in
        </p>
        <div className="mt-10 space-y-5">
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className={inputCls}
          />
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className={inputCls}
          />
        </div>
        {error && (
          <p data-testid="admin-login-error" className="mt-5 text-sm text-red-700">{error}</p>
        )}
        <button
          data-testid="admin-login-button"
          type="submit"
          disabled={loading}
          className="mt-8 w-full bg-navy text-white text-xs font-bold uppercase tracking-[0.2em] px-8 py-5 hover:bg-navy-dark transition-colors duration-300 disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
        <a href="/" data-testid="admin-back-link" className="block mt-6 text-xs text-navy-dark/50 hover:text-navy transition-colors duration-300">
          ← Back to website
        </a>
      </form>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("enquiries");
  const [data, setData] = useState({ enquiries: null, applications: null });

  const load = useCallback(
    async (t = token) => {
      try {
        const headers = { Authorization: `Bearer ${t}` };
        const [enq, apps] = await Promise.all([
          axios.get(`${API}/admin/enquiries`, { headers }),
          axios.get(`${API}/admin/applications`, { headers }),
        ]);
        setData({ enquiries: enq.data, applications: apps.data });
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
        } else {
          toast.error("Could not load messages.");
        }
      }
    },
    [token]
  );

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const markRead = async (id) => {
    try {
      await axios.patch(
        `${API}/admin/${tab}/${id}`,
        { status: "read" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData((d) => ({ ...d, [tab]: d[tab].map((e) => (e.id === id ? { ...e, status: "read" } : e)) }));
    } catch {
      toast.error("Could not update.");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginForm onLogin={setToken} />;

  const items = data[tab];
  const newCount = (items || []).filter((e) => e.status === "new").length;
  const counts = {
    enquiries: (data.enquiries || []).filter((e) => e.status === "new").length,
    applications: (data.applications || []).filter((e) => e.status === "new").length,
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-silver-light">
      <header className="bg-navy-dark text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo-white.png" alt="Karen's Caring Hands, LLC" className="h-11 w-auto" />
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/50">
              Enquiries dashboard
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={() => load()}
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-white/70 hover:text-white transition-colors duration-300 px-4 py-2.5"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] bg-white/10 hover:bg-white/20 transition-colors duration-300 px-4 py-2.5"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex gap-px bg-silver border border-silver max-w-md mb-10">
          <button
            data-testid="admin-tab-enquiries"
            onClick={() => setTab("enquiries")}
            className={`flex-1 px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
              tab === "enquiries" ? "bg-navy-dark text-white" : "bg-white text-navy-dark/60 hover:text-navy"
            }`}
          >
            Enquiries{counts.enquiries > 0 && ` (${counts.enquiries})`}
          </button>
          <button
            data-testid="admin-tab-applications"
            onClick={() => setTab("applications")}
            className={`flex-1 px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
              tab === "applications" ? "bg-navy-dark text-white" : "bg-white text-navy-dark/60 hover:text-navy"
            }`}
          >
            Applications{counts.applications > 0 && ` (${counts.applications})`}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-px bg-silver border border-silver max-w-md mb-10">
          <div className="bg-white p-6">
            <p className="font-display text-4xl text-navy-dark leading-none" data-testid="admin-total-count">
              {items ? items.length : "—"}
            </p>
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-navy-dark/50 mt-2">
              Total {tab}
            </p>
          </div>
          <div className="bg-white p-6">
            <p className="font-display text-4xl text-navy leading-none" data-testid="admin-new-count">
              {items ? newCount : "—"}
            </p>
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-navy-dark/50 mt-2">
              Awaiting reply
            </p>
          </div>
        </div>

        {items === null ? (
          <p className="text-sm text-navy-dark/60">Loading…</p>
        ) : items.length === 0 ? (
          <div data-testid="admin-empty-state" className="bg-white border border-silver p-16 text-center">
            <Inbox className="w-8 h-8 text-silver mx-auto" />
            <p className="mt-4 font-display text-2xl text-navy-dark">
              {tab === "enquiries" ? "No enquiries yet" : "No applications yet"}
            </p>
            <p className="mt-2 text-sm text-navy-dark/60">
              {tab === "enquiries"
                ? "New messages from the website contact form will appear here."
                : "New job applications from the website careers form will appear here."}
            </p>
          </div>
        ) : (
          <div className="space-y-px bg-silver border border-silver">
            {items.map((e) => (
              <article
                key={e.id}
                data-testid={`enquiry-row-${e.id}`}
                className="bg-white p-6 md:p-8 flex flex-col lg:flex-row lg:items-start gap-6"
              >
                <div className="lg:w-48 shrink-0">
                  <p className="text-sm font-bold text-navy-dark">{e.name}</p>
                  <p className="text-xs text-navy-dark/60 mt-1">{e.email}</p>
                  {e.phone && <p className="text-xs text-navy-dark/60 mt-0.5">{e.phone}</p>}
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-navy-dark/40 mt-3">
                    {new Date(e.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex-1">
                  {(e.service || e.position) && (
                    <span className="inline-block text-[10px] font-mono uppercase tracking-[0.2em] bg-silver-light text-navy px-3 py-1.5 mb-3">
                      {e.service || [e.position, e.availability].filter(Boolean).join(" · ")}
                    </span>
                  )}
                  <p className="text-sm leading-relaxed text-navy-dark/80 whitespace-pre-wrap">{e.message}</p>
                  {(e.certifications || e.drivers_license || e.references) && (
                    <div className="mt-3 space-y-1">
                      {e.certifications && (
                        <p className="text-xs text-navy-dark/60"><span className="font-bold">Certifications:</span> {e.certifications}</p>
                      )}
                      {e.drivers_license && (
                        <p className="text-xs text-navy-dark/60"><span className="font-bold">License & transportation:</span> {e.drivers_license}</p>
                      )}
                      {e.references && (
                        <p className="text-xs text-navy-dark/60 whitespace-pre-wrap"><span className="font-bold">References:</span> {e.references}</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="shrink-0 flex lg:flex-col items-center lg:items-end gap-3">
                  <span
                    data-testid={`enquiry-status-${e.id}`}
                    className={`text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-1.5 ${
                      e.status === "new" ? "bg-navy text-white" : "bg-silver-light text-navy-dark/60"
                    }`}
                  >
                    {e.status}
                  </span>
                  {e.status === "new" && (
                    <button
                      data-testid={`enquiry-mark-read-${e.id}`}
                      onClick={() => markRead(e.id)}
                      className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-navy hover:text-navy-dark transition-colors duration-300"
                    >
                      <MailOpen className="w-3.5 h-3.5" /> Mark read
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
